'use client';

import { useState, useCallback } from 'react';
import { LinkIcon } from '@/components/Icons/Icons';

interface CopyLinkProps {
  /** Optional custom URL to copy. Defaults to current page URL */
  url?: string;
  /** Additional CSS classes */
  className?: string;
  /** Icon size classes (default: uses LinkIcon default size) */
  iconClassName?: string;
}

/** Copies the current page URL (or a custom `url`) to the clipboard and shows brief "Copied!" feedback. */
export default function CopyLink({ url, className = '', iconClassName = '' }: CopyLinkProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    try {
      let linkToCopy: string;
      if (url) {
        linkToCopy =
          typeof window !== 'undefined' && !url.startsWith('http')
            ? `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`
            : url;
      } else {
        linkToCopy = typeof window !== 'undefined' ? window.location.href : '';
      }

      if (!linkToCopy) return;

      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      // Failed to copy link
    }
  }, [url]);

  return (
    <div 
      onClick={handleCopyLink}
      className={`cursor-pointer relative group ${className}`}
      role="button"
      aria-label={copied ? 'Link copied!' : 'Copy link'}
      title={copied ? 'Link copied!' : 'Copy link'}
    >
      <LinkIcon 
        className={`${iconClassName || 'text-[var(--Icons-Medium)]'} ${copied ? 'text-[var(--Color-Extended-Green-Green-Shade)]' : ''} transition-colors duration-200`}
      />

      {copied && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-surface-5 text-text-default-strong text-xs px-2 py-1 rounded whitespace-nowrap z-10">
          Copied!
        </div>
      )}
    </div>
  );
}
